<template>
  <div class="ai-result">
    <div class="ai-verdict">
      <div class="ai-grade">
        <span>{{ intent.level === 'unknown' ? '待判断' : intent.level + ' 级' }}</span>
        <small>AI 建议 · {{ confidence }}</small>
      </div>
      <div class="ai-verdict-side">
        <span class="tag brand">{{ priority }}</span>
        <small>分析时人工等级 {{ result.current_grade || '未填写' }}</small>
      </div>
      <p v-if="tab === 'advice'" class="ai-summary">{{ analysis.summary.text }}</p>
      <ai-evidence v-if="tab === 'advice'" :refs="analysis.summary.evidence" :sources="result.sources" />
    </div>

    <button v-if="tab === 'advice' && profileCount" type="button" class="ai-profile-link" @click="tab = 'profile'">有 {{ profileCount }} 项画像建议，核对后填入 <app-icon name="arrow" /></button>

    <div class="ai-tabs seg" role="tablist" aria-label="分析内容">
      <button type="button" role="tab" :aria-selected="tab === 'advice' ? 'true' : 'false'" :class="{ on: tab === 'advice' }" @click="tab = 'advice'">下一次怎么跟进</button>
      <button type="button" role="tab" :aria-selected="tab === 'profile' ? 'true' : 'false'" :class="{ on: tab === 'profile' }" @click="tab = 'profile'">画像与依据</button>
    </div>

    <div v-if="tab === 'advice'" class="ai-pane">
      <section class="ai-section">
        <div class="ai-section-head"><h3>这次先推进一件事</h3><span class="tag soft">{{ channel }}</span></div>
        <p class="ai-goal">{{ follow.goal }}</p>
        <p class="ai-secondary">{{ follow.reason }}</p>
      </section>
      <section class="ai-section ai-timing">
        <div class="ai-section-head"><h3>什么时候联系</h3><span class="tag">{{ timingKind }}</span></div>
        <p>{{ follow.timing.text }}</p>
        <b v-if="follow.timing.scheduled_at" class="ai-date">{{ timeText(follow.timing.scheduled_at) }}</b>
        <p class="ai-secondary">{{ follow.timing.reason }}</p>
        <ai-evidence :refs="follow.timing.evidence" :sources="result.sources" />
        <button class="button small soft" type="button" :disabled="disabled || stale || (!!follow.timing.scheduled_at && !usableTime)" @click="$emit('schedule', usableTime)">{{ follow.timing.scheduled_at ? '填入下次跟进时间' : '选择下次跟进时间' }}</button>
        <p class="ai-caption">{{ follow.timing.scheduled_at && !usableTime ? '建议时间已过，请重新分析' : '销售确认并保存后，才会更新日程' }}</p>
      </section>
      <section class="ai-section">
        <div class="ai-section-head">
          <h3>跟进可以这样说</h3>
          <button class="text-button" type="button" :disabled="disabled" @click="copyScript">复制话术</button>
        </div>
        <div class="ai-script-tabs" role="group" aria-label="话术渠道">
          <button type="button" :class="{ on: script === 'wechat' }" :aria-pressed="script === 'wechat' ? 'true' : 'false'" @click="script = 'wechat'">微信消息</button>
          <button type="button" :class="{ on: script === 'phone' }" :aria-pressed="script === 'phone' ? 'true' : 'false'" @click="script = 'phone'">电话开场</button>
        </div>
        <p class="ai-script">{{ scriptText }}</p>
        <textarea v-if="manualCopy" class="ai-copy-field" readonly :value="scriptText" aria-label="长按复制话术" />
        <p v-if="manualCopy" class="ai-caption">浏览器未允许自动复制，请长按上方文字复制</p>
      </section>
      <section v-if="follow.questions.length" class="ai-section">
        <h3>重点补问</h3>
        <ol class="ai-questions"><li v-for="(question, index) in follow.questions" :key="index">{{ question }}</li></ol>
      </section>
      <section v-if="follow.objection_responses.length" class="ai-section">
        <h3>客户有顾虑时</h3>
        <div v-for="(item, index) in follow.objection_responses" :key="index" class="ai-objection">
          <small class="ai-caption">{{ item.kind === 'explicit' ? '客户明确表达' : '可能的顾虑 · 待核实' }}</small>
          <b>{{ item.objection }}</b><p>{{ item.response }}</p>
          <ai-evidence :refs="item.evidence" :sources="result.sources" />
        </div>
      </section>
    </div>

    <div v-else class="ai-pane">
      <section class="ai-section">
        <customer-ai-profile ref="profileFill" :result="result" :values="profileValues" :choices="profileChoices" :disabled="disabled || result.meta.persistence_pending" :stale="stale" :detail="detail" hide-action @apply="$emit('profile', $event)" @selection="profileSelectionChanged" />
      </section>
      <section class="ai-section">
        <h3>购买 / 租赁意向判断</h3>
        <p>{{ intent.reason }}</p>
        <ai-evidence :refs="intent.evidence" :sources="result.sources" />
        <p class="ai-caption">AI 参考等级，由销售判断是否调整人工等级</p>
        <details class="ai-evidence">
          <summary>查看 AI 等级参考标准</summary>
          <p v-for="(description, level) in result.intent_levels" :key="level" class="ai-rubric"><b>{{ level }} 级</b> {{ description }}</p>
        </details>
      </section>
      <section class="ai-section">
        <h3>跟进优先级 · {{ priority }}</h3>
        <p>{{ analysis.priority.reason }}</p>
        <ai-evidence :refs="analysis.priority.evidence" :sources="result.sources" />
      </section>
      <section class="ai-section">
        <h3>客户画像</h3>
        <p v-if="!analysis.profile.length" class="ai-secondary">资料较少，先核实关键需求和决策安排。</p>
        <div v-for="(item, index) in analysis.profile" :key="index" class="ai-profile-row">
          <small>{{ item.label }}</small><p>{{ item.value }}</p>
          <ai-evidence :refs="item.evidence" :sources="result.sources" />
        </div>
      </section>
      <section v-for="group in factGroups" :key="group.title" class="ai-section">
        <h3>{{ group.title }}</h3>
        <div v-for="(item, index) in group.items" :key="index" class="ai-objection">
          <p>{{ item.text }}</p><ai-evidence :refs="item.evidence" :sources="result.sources" />
        </div>
      </section>
      <section class="ai-section">
        <details class="ai-evidence">
          <summary>查看本次纳入的 {{ result.sources.length }} 份资料</summary>
          <div v-for="source in result.sources" :key="source.id" class="ai-source"><b>{{ source.label }}</b><p>{{ source.text }}</p></div>
        </details>
      </section>
    </div>
  </div>
</template>
<script>
import AiEvidence from './ai-evidence'
import CustomerAiProfile from './customer-ai-profile'
import { profileRows } from '../common/ai-profile'
import { validAiTime } from '../common/ai'
import { dateTimeText } from '../common/when'
export default {
  components: { AiEvidence, CustomerAiProfile },
  props: {
    result: { type: Object, required: true }, disabled: Boolean, stale: Boolean, detail: Boolean,
    profileValues: { type: Object, default: () => ({}) }, profileChoices: { type: Object, default: () => ({}) }
  },
  data() { return { tab: 'advice', script: 'wechat', manualCopy: false, profileSelectedCount: 0 } },
  computed: {
    analysis() { return this.result.analysis },
    profileCount() { return profileRows(this.result, this.profileValues, this.profileChoices).length },
    intent() { return this.analysis.intent },
    follow() { return this.analysis.follow_up },
    confidence() { return { high: '依据较充分', medium: '依据一般', low: '资料待核实' }[this.intent.confidence] || '资料待核实' },
    priority() { return { high: '优先跟进', normal: '正常跟进', nurture: '持续培育' }[this.analysis.priority.level] || '待判断' },
    channel() { return { phone: '建议电话', wechat: '建议微信', visit: '建议约见', confirm: '先确认渠道' }[this.follow.channel] || '先确认渠道' },
    timingKind() { return { agreed: '记录中的约定', existing: '系统已有安排', suggested: 'AI 建议', unknown: '时间待确认' }[this.follow.timing.kind] || 'AI 建议' },
    usableTime() { return validAiTime(this.follow.timing.scheduled_at) },
    scriptText() { return this.script === 'wechat' ? this.follow.wechat_script : this.follow.phone_script },
    factGroups() { return [{ title: '需求变化与冲突', items: this.analysis.changes }, { title: '当前主要阻碍', items: this.analysis.blockers }].filter(group => group.items.length) }
  },
  watch: {
    tab() { this.reportProfileSelection() },
    profileCount() { this.reportProfileSelection() }
  },
  mounted() { this.reportProfileSelection() },
  methods: {
    profileSelectionChanged(state) { this.profileSelectedCount = state.count; this.reportProfileSelection() },
    reportProfileSelection() { this.$emit('profile-selection', { active: this.tab === 'profile' && this.profileCount > 0, count: this.profileSelectedCount }) },
    applyProfileSelection() { if (this.$refs.profileFill) this.$refs.profileFill.apply() },
    timeText(value) { return dateTimeText(value) },
    async copyScript() {
      try {
        if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(this.scriptText)
        else {
          const field = document.createElement('textarea')
          field.value = this.scriptText
          field.style.cssText = 'position:fixed;top:0;left:0;opacity:0;font-size:16px'
          document.body.appendChild(field)
          field.focus()
          field.select()
          const copied = document.execCommand('copy')
          document.body.removeChild(field)
          if (!copied) throw new Error('copy')
        }
        this.manualCopy = false
        this.$toast('话术已复制，请核对后使用')
      } catch (e) { this.manualCopy = true }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/customer-ai-result.vue